import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, X, Clock, Monitor, ShieldCheck, Award, CalendarDays, ArrowRight, AlertCircle, RotateCw, GraduationCap } from "lucide-react";
import { toast } from "sonner";
import { api, formatDate, scrollToId } from "../lib/app";
import { Reveal, MaskedLine, Eyebrow, EASE } from "../components/Motion";

const CLEARANCE_STYLES = {
  "Public Trust": "border-cleargreen/40 text-cleargreen",
  "Secret": "border-amberclass/50 text-amberclass",
  "Top Secret": "border-federalred/40 text-federalred",
  "TS/SCI": "border-federalred/40 text-federalred",
};

const Programs = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [category, setCategory] = useState("All");
  const [clearance, setClearance] = useState("All");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [regCourse, setRegCourse] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", organization: "", clearance: "Public Trust", message: "" });

  const load = () => {
    setLoading(true);
    setLoadError(false);
    api
      .get("/courses")
      .then((r) => setCourses(r.data))
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  useEffect(() => {
    document.body.style.overflow = selected ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [selected]);

  const categories = useMemo(
    () => ["All", ...Array.from(new Set(courses.map((c) => c.category)))],
    [courses]
  );

  const filtered = useMemo(
    () =>
      courses.filter((c) => {
        if (category !== "All" && c.category !== category) return false;
        if (clearance !== "All" && c.clearance !== clearance) return false;
        if (query && !`${c.code} ${c.title} ${c.description}`.toLowerCase().includes(query.toLowerCase())) return false;
        return true;
      }),
    [courses, category, clearance, query]
  );

  const openEnroll = (course) => {
    setSelected(null);
    setRegCourse(course.code);
    setTimeout(() => scrollToId("register"), 120);
  };

  const submitRegistration = async (e) => {
    e.preventDefault();
    if (!regCourse) {
      toast.error("Please select a program to enroll in.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/registrations", { ...form, course_code: regCourse });
      toast.success(res.data.message, { description: `${res.data.course_code} — ${res.data.course_title}` });
      setForm({ name: "", email: "", organization: "", clearance: "Public Trust", message: "" });
      setRegCourse("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed. Please verify your details and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="programs-page">
      {/* Header band */}
      <section className="relative bg-navy-950 grid-lines overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-44 pb-20 lg:pt-52 lg:pb-24">
          <MaskedLine as="div" delay={0.3} className="mb-6">
            <Eyebrow light>Course Catalog · 2026 Sessions</Eyebrow>
          </MaskedLine>
          <h1 className="font-serif font-bold tracking-tight leading-[1.08] text-4xl sm:text-5xl lg:text-6xl text-white max-w-3xl" data-testid="programs-headline">
            <MaskedLine delay={0.45}>Programs built for</MaskedLine>
            <MaskedLine delay={0.57}>
              <em className="text-gold-300 italic">national defense.</em>
            </MaskedLine>
          </h1>
          <MaskedLine delay={0.7} className="mt-8 max-w-2xl">
            <p className="text-base sm:text-lg leading-relaxed text-slate-300">
              Eight accredited career tracks across defensive operations,
              adversary emulation, zero trust, industrial control systems and
              executive leadership. Filter by category, clearance and keyword.
            </p>
          </MaskedLine>
        </div>
      </section>

      {/* Catalog */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20" data-testid="catalog-section">
        {/* Controls */}
        <div className="flex flex-col lg:flex-row gap-5 lg:items-center lg:justify-between" data-testid="catalog-controls">
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setCategory(cat)}
                data-testid={`catalog-tab-${cat.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`px-4 h-10 text-[12px] font-mono uppercase tracking-[0.12em] border transition-all duration-200 ${
                  category === cat
                    ? "bg-navy-900 text-gold-300 border-navy-900"
                    : "bg-white text-slate-600 border-paper-border hover:border-gold-500/60 hover:text-navy-900"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
          <div className="flex flex-col sm:flex-row gap-4 shrink-0">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search curriculum…"
                data-testid="catalog-search-input"
                className="field-input pl-9 sm:w-64"
              />
            </div>
            <select
              value={clearance}
              onChange={(e) => setClearance(e.target.value)}
              data-testid="catalog-clearance-filter"
              className="field-input sm:w-44"
              aria-label="Filter by clearance level"
            >
              {["All", "Public Trust", "Secret", "Top Secret"].map((c) => (
                <option key={c} value={c}>{c === "All" ? "All Clearances" : c}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white border border-paper-border p-8 animate-pulse space-y-4">
                <div className="h-6 w-24 bg-paper-200" />
                <div className="h-7 w-3/4 bg-paper-200" />
                <div className="h-4 w-full bg-paper-100" />
                <div className="h-4 w-5/6 bg-paper-100" />
                <div className="h-4 w-2/3 bg-paper-100" />
              </div>
            ))}
          </div>
        ) : loadError ? (
          <div className="mt-12 bg-white border border-paper-border p-12 text-center" data-testid="catalog-error">
            <AlertCircle className="w-8 h-8 text-federalred mx-auto" />
            <p className="mt-4 text-sm text-slate-600">Course data could not be loaded.</p>
            <button
              onClick={load}
              data-testid="catalog-retry-button"
              className="mt-6 inline-flex items-center gap-2 px-6 h-11 rounded-full bg-navy-900 text-gold-300 text-sm font-semibold transition-all duration-200 hover:bg-navy-800"
            >
              <RotateCw className="w-4 h-4" /> Retry
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="mt-12 bg-white border border-paper-border p-12 text-center" data-testid="catalog-empty">
            <p className="text-sm text-slate-600">No programs match your filters. Adjust the search or clearance level.</p>
          </div>
        ) : (
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="catalog-grid">
            {filtered.map((c, i) => (
              <Reveal key={c.code} delay={0.05 * (i % 3)} className="h-full">
                <div
                  data-testid={`course-card-${c.code.toLowerCase()}`}
                  className="group h-full bg-white border border-paper-border p-8 flex flex-col transition-all duration-300 hover:border-gold-500/60 hover:-translate-y-1 hover:shadow-xl hover:shadow-gold-500/10"
                >
                  <div className="flex items-center justify-between">
                    <span className="chip border-gold-500/50 text-gold-600">{c.code}</span>
                    <span className={`chip ${CLEARANCE_STYLES[c.clearance] || "border-paper-border text-slate-500"}`}>
                      <ShieldCheck className="w-3 h-3" /> {c.clearance}
                    </span>
                  </div>
                  <h3 className="mt-5 font-serif font-semibold text-xl text-navy-900 leading-snug">{c.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600 line-clamp-3 flex-1">{c.description}</p>
                  <div className="mt-6 pt-5 border-t border-paper-border flex items-center justify-between">
                    <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-slate-500">
                      {c.duration_weeks} wks · {c.format}
                    </span>
                    <button
                      onClick={() => setSelected(c)}
                      data-testid={`course-syllabus-button-${c.code.toLowerCase()}`}
                      className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-gold-600 transition-colors duration-200 hover:text-navy-900"
                    >
                      Syllabus <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5" />
                    </button>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        )}
      </section>

      {/* Registration */}
      <section id="register" className="bg-navy-950 border-y border-gold-500/20 grid-lines scroll-mt-24" data-testid="register-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <Reveal>
                <Eyebrow light>Official Enrollment</Eyebrow>
              </Reveal>
              <MaskedLine as="h2" inView delay={0.1} className="mt-4 font-serif font-bold tracking-tight text-3xl sm:text-4xl text-white leading-tight">
                Request training on behalf of your agency.
              </MaskedLine>
              <Reveal delay={0.2}>
                <p className="mt-5 text-sm sm:text-base leading-relaxed text-slate-300 max-w-md">
                  Submit an enrollment request and the Academy admissions office
                  will begin clearance verification within two business days.
                  Agency training funds, individual development plans and
                  self-sponsored candidates are all accepted pathways.
                </p>
              </Reveal>
              <Reveal delay={0.3}>
                <div className="mt-8 space-y-3">
                  {["Seats allocated on first-confirmed, first-adjudicated basis", "Clearance verification handled through your security office", "Certificates issue with CPE credit upon graduation"].map((t) => (
                    <div key={t} className="flex items-center gap-3 text-sm text-slate-300">
                      <span className="w-1.5 h-1.5 rotate-45 bg-gold-500 shrink-0" /> {t}
                    </div>
                  ))}
                </div>
              </Reveal>
            </div>

            <Reveal className="lg:col-span-7" delay={0.15}>
              <form
                onSubmit={submitRegistration}
                data-testid="registration-form"
                className="bg-white border border-gold-500/30 p-8 lg:p-10 shadow-2xl shadow-navy-950/30"
              >
                <div className="flex items-center gap-3 pb-6 border-b border-paper-border">
                  <GraduationCap className="w-5 h-5 text-gold-600" />
                  <h3 className="font-serif font-semibold text-lg text-navy-900">Enrollment Request — Form FA-1</h3>
                </div>
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="field-label" htmlFor="reg-name">Full Name</label>
                    <input id="reg-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="field-input" placeholder="Jane A. Doe" data-testid="registration-name-input" />
                  </div>
                  <div>
                    <label className="field-label" htmlFor="reg-email">Government Email</label>
                    <input id="reg-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="field-input" placeholder="jane.doe@agency.gov" data-testid="registration-email-input" />
                  </div>
                  <div>
                    <label className="field-label" htmlFor="reg-org">Agency / Organization</label>
                    <input id="reg-org" required value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} className="field-input" placeholder="Dept. of …" data-testid="registration-org-input" />
                  </div>
                  <div>
                    <label className="field-label" htmlFor="reg-clearance">Current Clearance</label>
                    <select id="reg-clearance" value={form.clearance} onChange={(e) => setForm({ ...form, clearance: e.target.value })} className="field-input" data-testid="registration-clearance-select">
                      {["Undetermined", "Public Trust", "Secret", "Top Secret", "TS/SCI"].map((c) => <option key={c}>{c}</option>)}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="field-label" htmlFor="reg-course">Program of Interest</label>
                    <select id="reg-course" required value={regCourse} onChange={(e) => setRegCourse(e.target.value)} className="field-input" data-testid="registration-course-select">
                      <option value="">Select a program…</option>
                      {courses.map((c) => (
                        <option key={c.code} value={c.code}>{c.code} — {c.title}</option>
                      ))}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="field-label" htmlFor="reg-msg">Funding Authority / Notes</label>
                    <textarea id="reg-msg" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="field-textarea" placeholder="Training budget line, supervisor contact, or cohort request…" data-testid="registration-message-input" />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="registration-submit-button"
                  className="mt-8 w-full h-12 rounded-full bg-gold-500 text-navy-900 text-sm font-semibold tracking-wide transition-all duration-200 hover:bg-gold-400 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-gold-500/25 disabled:opacity-60 disabled:pointer-events-none"
                >
                  {submitting ? "Submitting…" : "Submit Enrollment Request"}
                </button>
                <p className="mt-4 text-[11px] leading-relaxed text-slate-500 text-center">
                  Submissions are recorded upon receipt. This is a training
                  environment; no federal adjudication occurs from this form.
                </p>
              </form>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Course modal */}
      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[90] bg-navy-950/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
            onClick={() => setSelected(null)}
            data-testid="course-detail-modal-backdrop"
          >
            <motion.div
              initial={{ y: 60, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 60, opacity: 0 }}
              transition={{ duration: 0.35, ease: EASE }}
              onClick={(e) => e.stopPropagation()}
              data-testid="course-detail-modal"
              className="relative bg-white w-full sm:max-w-2xl max-h-[88vh] overflow-y-auto border-t-4 sm:border-t-0 sm:border border-gold-500/60"
            >
              <button
                onClick={() => setSelected(null)}
                data-testid="course-modal-close"
                aria-label="Close syllabus"
                className="absolute top-4 right-4 z-10 p-2 text-slate-500 transition-colors duration-200 hover:text-navy-900"
              >
                <X className="w-5 h-5" />
              </button>
              <div className="bg-navy-950 p-8">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="chip border-gold-500/50 text-gold-300">{selected.code}</span>
                  <span className="chip border-white/20 text-slate-200">{selected.level}</span>
                  <span className="chip border-white/20 text-slate-200">{selected.category}</span>
                </div>
                <h3 className="mt-4 font-serif font-bold text-2xl sm:text-3xl text-white leading-snug pr-10">
                  {selected.title}
                </h3>
              </div>
              <div className="p-8">
                <p className="text-sm sm:text-base leading-relaxed text-slate-700">{selected.description}</p>

                <div className="mt-7 grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {[
                    { icon: Clock, label: "Duration", value: `${selected.duration_weeks} weeks` },
                    { icon: Monitor, label: "Format", value: selected.format },
                    { icon: Award, label: "CPE Credits", value: String(selected.cpe_credits) },
                    { icon: CalendarDays, label: "Next Session", value: formatDate(selected.next_session) },
                  ].map((m) => (
                    <div key={m.label} className="border border-paper-border p-4">
                      <m.icon className="w-4 h-4 text-gold-600" />
                      <p className="mt-2 font-mono text-[9px] uppercase tracking-[0.16em] text-slate-500">{m.label}</p>
                      <p className="mt-1 text-[13px] font-semibold text-navy-900 leading-snug">{m.value}</p>
                    </div>
                  ))}
                </div>

                <h4 className="mt-8 font-mono text-[11px] uppercase tracking-[0.2em] text-gold-600">Curriculum</h4>
                <ul className="mt-4 space-y-2.5">
                  {selected.syllabus.map((s, i) => (
                    <li key={s} className="flex gap-3 text-sm text-slate-700">
                      <span className="font-mono text-xs text-gold-600 mt-0.5 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                      {s}
                    </li>
                  ))}
                </ul>

                <h4 className="mt-8 font-mono text-[11px] uppercase tracking-[0.2em] text-gold-600">Prerequisites</h4>
                <ul className="mt-4 space-y-2">
                  {selected.prerequisites.map((p) => (
                    <li key={p} className="flex gap-2.5 text-sm text-slate-700">
                      <span className="w-1 h-1 rounded-full bg-gold-600 mt-2 shrink-0" /> {p}
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => openEnroll(selected)}
                  data-testid="course-modal-enroll-button"
                  className="mt-8 w-full h-12 rounded-full bg-navy-900 text-gold-300 text-sm font-semibold tracking-wide transition-all duration-200 hover:bg-navy-800 hover:-translate-y-0.5 hover:shadow-lg"
                >
                  Request Enrollment — {selected.code}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Programs;
